import { Alert, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Network from 'expo-network';

import { EnvVars } from '../constants/EnvConfig';
import EstablishSessionError from './EstablishSessionError';
import { FILTER_ALLOW_ALL } from './reducers/pantry';
import getInstallationIdAsync from '../utils/getInstallationIdAsync';
import { log, logError } from '../utils/logger';
import { clearLocalToken } from '../utils/token';

const { apiUrl } = EnvVars;

// Every existing call site passes a relative path (no scheme), so this only changes behavior for
// a new caller that explicitly needs a different host/base path than apiUrl -- e.g. the "For
// Neighbors" RPC service, which lives at the bare domain root rather than under apiUrl's /api/3.0/.
// An absolute URL is only ever accepted if it shares apiUrl's origin -- callers attach session
// headers (X-UIDH) before this runs, so an unrestricted absolute URL would silently forward the
// session token to whatever host the caller passed.
function resolveUrl(url) {
  if (!/^https?:\/\//.test(url)) return apiUrl + url;

  const requestedOrigin = new URL(url).origin;
  const trustedOrigin = new URL(apiUrl).origin;
  if (requestedOrigin !== trustedOrigin) {
    throw new Error(`Refusing to send session headers to untrusted host: ${requestedOrigin}`);
  }
  return url;
}

async function checkSession() {
  const expiresAt = await AsyncStorage.getItem('expiresAt');
  const sessionId = await AsyncStorage.getItem('sessionId');
  if (!expiresAt || !sessionId || parseInt(expiresAt, 10) <= Math.round(new Date().getTime() / 1000)) {
    const token = await AsyncStorage.getItem('token');
    if (token) return establishSession(token);
    return Promise.resolve();
  }
  return AsyncStorage.getItem('sessionId');
}

async function buildRequestPreamble(headers, needsSession) {
  const sessionHeaders = {};
  if (needsSession) {
    // TODO # 3230: Implement better, safer session handling
    const sessionId = await checkSession();
    if (!sessionId) throw new Error('No session available');
    sessionHeaders['X-UIDH'] = `SESSIONID=${sessionId}`;
  }

  // only proceed if we can reach the internet
  const NetworkState = await Network.getNetworkStateAsync();
  if (!NetworkState.isInternetReachable) {
    Alert.alert('You are not connected to the internet. Please connect and try again.');
    throw new Error('No internet connection');
  }

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...sessionHeaders,
    ...headers,
  };
}

/**
 * Method to conduct POST requests
 * @param {string}  url          The url path we send the request to
 * @param {object}  params       The body parameters
 * @param {object}  headers      The header parameters
 * @param {string}  method       Method, post or patch
 * @param {boolean} needsSession Does this request needs a session? Default true
 */
async function post(url, params, headers = {}, method = 'POST', needsSession = true) {
  const headerParams = await buildRequestPreamble(headers, needsSession);

  const requestParams = {
    method,
    headers: headerParams,
    body: JSON.stringify(params),
  };

  const response = await fetch(resolveUrl(url), requestParams);
  if (!response) return Promise.reject(new Error('No response received'));
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`);
    error.status = response.status;
    return Promise.reject(error);
  }
  return response.json().then((body) => ({ ...body, status: response.status }));
}

/**
 * Method to conduct GET requests
 * @param {string}  url          The url path we send the request to
 * @param {object}  headers      The header parameters
 * @param {boolean} needsSession Does this request needs a session? Default true
 * @param {number}  timeout      Time in ms to wait for fetch until throwing a timed-out error (optional)
 */
async function get(url, headers = {}, needsSession = true, timeout = undefined) {
  const headerParams = await buildRequestPreamble(headers, needsSession);

  const requestParams = {
    method: 'GET',
    headers: headerParams,
  };

  const fullUrl = resolveUrl(url);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(fullUrl, {
    ...requestParams,
    signal: timeout ? controller.signal : undefined,
  })
    .then((response) => {
      log('Api.get() FULFILLED', { statusCode: response?.status, url: fullUrl });
      if (!response) throw new Error('No response received');
      if (!response.ok) {
        const error = new Error(`Request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    })
    .catch(
      /**
       * @param {Error} error
       */
      (error) => {
        if (error.name === 'AbortError') log('Api.get() ABORT', { timeout, route: url.split('?')[0] });
        else
          log('Api.get() ERROR', {
            errorName: error?.name,
            errorMessage: error?.message,
            route: url.split('?')[0],
          });
        return Promise.reject(error);
      }
    )
    .finally(() => {
      clearTimeout(timeoutId);
    });
}

/**
 * Establishes a new session with the server using the provided token.
 *
 * @async
 * @function establishSession
 * @param {string} token - The auth token to exchange for a session.
 * @returns {Promise<string>} Resolves with the `sessionId` on success.
 * @throws {EstablishSessionError} `code` names the reason; `status` carries the HTTP status when one was received.
 *
 * @description
 * Sends a POST to `sessions/establish` and handles each outcome:
 * - On success (a `sessionId` and `expiresAt`), stores both in AsyncStorage and returns the `sessionId`.
 * - On a 4xx (except a 408 timeout), the server rejected the token: clears the local token and throws.
 * - On any other failure (transport error, no response, 408, 5xx, or a malformed body), throws an `EstablishSessionError` whose `code` (and `status`, when set) names the reason.
 *
 * @example
 * try {
 *   const sessionId = await establishSession(userToken);
 * } catch (error) {
 *   logError(error);
 * }
 */
async function establishSession(token) {
  log('Api:establishSession');
  let res;
  try {
    res = await post('sessions/establish', { token }, {}, 'POST', false);
  } catch (error) {
    const { status } = error;
    // The server rejected the token (any 4xx except a 408 timeout).
    if (status >= 400 && status < 500 && status !== 408) {
      clearLocalToken();
      throw new EstablishSessionError('token-rejected', { cause: error, status });
    }
    if (status === 408) throw new EstablishSessionError('timeout', { cause: error, status });
    if (status >= 500) throw new EstablishSessionError('server-error', { cause: error, status });
    // TODO(PlentifulApp/plentiful#3811): offline and every other untyped error are both 'transport-error'
    // here; give them distinct codes once post() surfaces typed outcomes.
    throw new EstablishSessionError('transport-error', { cause: error });
  }

  const { sessionId, expiresAt, status } = res;

  if (sessionId && expiresAt) {
    await AsyncStorage.setItem('sessionId', sessionId);
    await AsyncStorage.setItem('expiresAt', `${expiresAt}`);
    return sessionId;
  }
  // 2xx with no usable session (missing or only one of sessionId/expiresAt).
  throw new EstablishSessionError('malformed-payload', { status });
}

function selectLanguage(index) {
  return AsyncStorage.setItem('language', index.toString());
}

// no session
async function checkEmailOrPhone(searchString, language) {
  log('Api:checkEmailOrPhone');
  const deviceOS = Platform.OS;
  const deviceId = await getInstallationIdAsync();
  const params = { searchString, language, deviceOS, deviceId };
  return post('check/email_or_phone', params, {}, 'POST', false);
}

function otpSend(phone, deviceId, language) {
  const params = { phone, device_id: deviceId, language };
  return post('otp/send', params, {}, 'POST', false);
}

function otpConfirm(phone, code, deviceId, language, needsSession = false) {
  const params = { phone, code, language, device_id: deviceId, os: Platform.OS };
  return post('otp/verify', params, {}, 'POST', needsSession);
}

function emailVerified(params) {
  return post('users/email_verified', params, {}, 'POST', false);
}

function unsubscribeEmail(token, subscribed) {
  return post('user/unsubscribe/update', { token, subscribed }, {}, 'POST', false);
}

function createPassword(email, password, token) {
  // token is only used for reset password
  return post('users/create_password', { email, password, token }, {}, 'POST', false);
}

async function enterPassword(email, password, language) {
  const deviceId = await getInstallationIdAsync();
  const deviceOS = Platform.OS;
  const params = { email, password, language, deviceId, deviceOS };
  return post('users/login_password', params, {}, 'POST', false);
}

async function loginByMagicLink(token) {
  const deviceId = await getInstallationIdAsync();
  const deviceOS = Platform.OS;
  const params = { token, deviceId, deviceOS };
  return post('users/login_magic_link', params, {}, 'POST', false);
}

async function sendMagicLink(emailOrPhone, magicLinkMethod, language) {
  const params = { emailOrPhone, magicLinkMethod, language };
  return post('users/send_magic_link', params, {}, 'POST', false);
}

function resetPassword(email) {
  return post('users/reset_password', { email }, {}, 'POST', false);
}

function createUser(user) {
  log('Api:createUser');
  return post('users/create', { ...user, food_pick_up: false }, {}, 'POST', false);
}

// Email
function enterEmail(email, doSend) {
  const params = { email, do_send: doSend };
  return post('users/enter_email', params);
}

function enterPhone(phone) {
  return post('users/enter_phone', { phone });
}

function doNotAskAgain(type) {
  return post('users/do_not_ask_again', { type });
}

// Pantries
function getPantries(isLoggedIn, lat1, lng1, lat2, lng2, filter = FILTER_ALLOW_ALL, options = {}) {
  const { algoliaQuery, q, currentPosition, limit, offset } = options;

  log('Api.getPantries()', {
    queryLength: algoliaQuery?.length,
    limit,
    offset,
  });

  // Start with filter parameters
  const searchParams = Object.keys(filter).reduce((acc, filterKey) => {
    const specifiedFilters = filter[filterKey];

    // an empty array means "allow all", so use the default filter if specifiedFilters is empty
    const requestFilters = specifiedFilters.length > 0 ? specifiedFilters : FILTER_ALLOW_ALL[filterKey];
    // filter arrives as Record<string, string[]>, so join the values into querystring param
    acc[filterKey] = requestFilters.join(',');
    return acc;
  }, {});

  // Add bounding box only if provided (not null/undefined)
  // When algoliaQuery is present, bounding box should be omitted (mutually exclusive)
  if (lat1 != null && lng1 != null && lat2 != null && lng2 != null) {
    searchParams.lat1 = lat1;
    searchParams.lng1 = lng1;
    searchParams.lat2 = lat2;
    searchParams.lng2 = lng2;
  }

  // NEW: Add search query parameter (backend uses 'q' parameter, not 'algoliaQuery')
  if (algoliaQuery) {
    searchParams.q = algoliaQuery;
  } else if (q) {
    searchParams.q = q;
  }

  // NEW: Add currentPosition for distance-based sorting
  if (currentPosition) {
    searchParams.currentPosition = currentPosition;
  }

  // NEW: Add pagination parameters
  if (limit !== undefined) {
    searchParams.limit = limit;
  }
  if (offset !== undefined) {
    searchParams.offset = offset;
  }

  const url = `map/locations?${new URLSearchParams(searchParams)}`;
  return get(url, {}, isLoggedIn).then((response) => {
    if (response && response.length) return Promise.resolve(response);
    return Promise.resolve([]);
  });
}

function getPantryByOrganizationId(organizationId, isLoggedIn) {
  return get(`map/location/${organizationId}`, {}, isLoggedIn);
}

// User
function getCurrentUser() {
  return get(`users/current`);
}

function updateUser(updates) {
  return post('users/current', updates, {}, 'PATCH');
}

function pushToken(token) {
  return post('users/current/push_token_expo', { token }, {}, 'PATCH');
}

function mergeUsers(email, phone) {
  return post('users/merge', { email, phone });
}

function deleteAccount() {
  return post('users/account', {}, {}, 'DELETE');
}

// Eula
function acceptTerms() {
  return post('users/accept_eula', {});
}

function subscribeServiceNotifications(pantryId) {
  return post(`pantries/${pantryId}/service-notifications`, {});
}

function unsubscribeServiceNotifications(pantryId) {
  return post(`pantries/${pantryId}/service-notifications`, {}, {}, 'DELETE');
}

function getUpdates() {
  return get('update_threads');
}

function getThread(id) {
  return get(`update_threads/${id}/updates`);
}

// VISITS
function getVisits() {
  return get('visits').then((response) => {
    if (response && response.visit_list) return Promise.resolve(response.visit_list);
    return Promise.resolve([]);
  });
}

function getNextOptions(visitId) {
  return get(`next_visits/${visitId}`).then((response) => {
    if (response.status === 'success') {
      return Promise.resolve(response.results);
    }
    return undefined;
  });
}

function makeAppointment(pantryId, serviceDate, time) {
  return post('visits/create', {
    pantry_id: pantryId,
    date: serviceDate,
    time,
  }).then((response) => {
    if (response && response.errors) return Promise.reject(response.errors);
    return Promise.resolve(response.visit);
  });
}

function cancelVisit(visitId) {
  return post(`visits/${visitId}`, {}, {}, 'DELETE');
}

function changeAppointment(visitId, serviceDate, time) {
  return post(
    `visits/${visitId}/date`,
    {
      date: serviceDate,
      time,
    },
    {},
    'PATCH'
  ).then((response) => {
    if (response && response.errors) return Promise.reject(response.errors);
    return Promise.resolve(response.visit);
  });
}

// FEEDBACK
function sendFeedback(feedback) {
  return post('feedback/submit', { feedback });
}

// TEFAP
function getTefapConfig() {
  log('Api:tefapConfig');
  return get('tefap/config');
}

// ZIPCODE

/**
 * Get the state of a given US zipcode
 * @param zipcode
 * @returns {Promise<string | null> state as two-character code, null if none found
 */
async function zipcodeToState(zipcode) {
  let response;
  try {
    response = await get(`zipcode_to_state/${zipcode}`, {}, true, 5000);
  } catch (error) {
    logError(new Error('[Api.zipcodeToState()] Error during fetch', { cause: error }));
    return null;
  }
  return response?.state ?? null;
}

export default {
  establishSession,
  selectLanguage,
  checkEmailOrPhone,
  enterEmail,
  enterPhone,
  doNotAskAgain,
  emailVerified,
  unsubscribeEmail,
  createPassword,
  enterPassword,
  sendMagicLink,
  loginByMagicLink,
  resetPassword,
  createUser,
  getPantries,
  getPantryByOrganizationId,
  getCurrentUser,
  updateUser,
  deleteAccount,
  pushToken,
  otpConfirm,
  otpSend,
  mergeUsers,
  acceptTerms,
  subscribeServiceNotifications,
  unsubscribeServiceNotifications,
  getVisits,
  getNextOptions,
  makeAppointment,
  cancelVisit,
  changeAppointment,
  sendFeedback,
  getUpdates,
  getThread,
  getTefapConfig,
  post,
  get,
  checkSession,
  zipcodeToState,
};
